import { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient } from '../../auth-lib';
import { Button, Card, Input, Modal, Select, Skeleton, useToast } from '../../design-system';

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '';

const fmtSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const MAX_BYTES = 25 * 1024 * 1024; // mirrors the multer limit server-side

// Documents attached to a task.
//
// These are real rows in the firm's document store (`/api/documents` with
// linkedToType 'Task'), not the legacy inline `task.attachments[]` array — so
// they get the same storage, S3 + OneDrive mirroring, categorisation, access
// logging and soft-delete as every other document. The task's client is
// inherited automatically, which is what files them in the right place.
function TaskDocumentsTab({ task, locked }) {
  const toast = useToast();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkForm, setLinkForm] = useState({ fileName: '', fileUrl: '', category: 'OTHER' });
  const [category, setCategory] = useState('OTHER');
  // Fetched rather than mirrored in a constants file — DocumentsManager does
  // the same, and one taxonomy copied into the frontend is one that drifts.
  const [categories, setCategories] = useState([]);
  const fileRef = useRef(null);

  useEffect(() => {
    apiClient
      .get('/api/documents/categories')
      .then((r) => setCategories(r.data?.categories || []))
      .catch(() => {});
  }, []);

  // A task carries its client on either field; documents inherit it so they
  // file under the right client rather than as loose firm-level files.
  const clientId = task.clientId?._id || task.clientIds?.[0]?._id || null;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiClient.get('/api/documents', {
        params: { linkedToType: 'Task', linkedToId: task._id },
      });
      setDocs(res.data || []);
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task._id]);

  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    // Checked here as well as server-side so a 25MB+ file fails instantly
    // rather than after a long upload.
    if (file.size > MAX_BYTES) {
      toast.danger('That file is larger than the 25MB limit');
      return;
    }
    setUploading(true);
    try {
      const body = new FormData();
      body.append('file', file);
      body.append('category', category);
      body.append('linkedToType', 'Task');
      body.append('linkedToId', task._id);
      if (clientId) body.append('clientId', clientId);
      if (task.period) body.append('period', task.period);

      // No explicit Content-Type: axios must set it itself so the multipart
      // BOUNDARY is included. Hard-coding 'multipart/form-data' drops the
      // boundary and the server can't parse the body.
      // The client's default 10s timeout is fine for a form post but not for a
      // 25MB file on a slow line, so this one call gets a longer one.
      await apiClient.post('/api/documents', body, { timeout: 120000 });
      toast.success('Document uploaded');
      await load();
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const addLink = async () => {
    if (!linkForm.fileUrl.trim()) {
      toast.danger('Enter the link URL');
      return;
    }
    try {
      await apiClient.post('/api/documents/link', {
        fileName: linkForm.fileName.trim() || linkForm.fileUrl.trim(),
        fileUrl: linkForm.fileUrl.trim(),
        category: linkForm.category,
        linkedToType: 'Task',
        linkedToId: task._id,
        clientId: clientId || undefined,
      });
      setLinkOpen(false);
      setLinkForm({ fileName: '', fileUrl: '', category: 'OTHER' });
      toast.success('Link added');
      await load();
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to add link');
    }
  };

  // Downloads go through the API so the access is logged and the storage key
  // is never exposed to the browser.
  const download = async (doc) => {
    if (doc.source === 'link' && doc.fileUrl) {
      window.open(doc.fileUrl, '_blank', 'noreferrer');
      return;
    }
    try {
      const res = await apiClient.get(`/api/documents/${doc.id || doc._id}/url`);
      if (res.data?.url) window.open(res.data.url, '_blank', 'noreferrer');
      else toast.danger('No download link available');
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to open document');
    }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Remove "${doc.title || doc.fileName}"? It can be restored from Documents.`)) return;
    try {
      await apiClient.delete(`/api/documents/${doc.id || doc._id}`);
      setDocs((prev) => prev.filter((d) => (d.id || d._id) !== (doc.id || doc._id)));
      toast.success('Document removed');
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to remove document');
    }
  };

  return (
    <Card padding="md">
      <div className="td-card-head">
        <span className="td-card-title">
          Documents {docs.length > 0 && <span className="td-count">{docs.length}</span>}
        </span>
        {!locked && (
          <div className="td-doc-actions">
            <Select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="td-doc-category"
              title="Category for the next upload"
            >
              {categories.map((c) => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </Select>
            <input
              ref={fileRef}
              type="file"
              className="td-doc-file"
              onChange={(e) => upload(e.target.files?.[0])}
              disabled={uploading}
            />
            <Button variant="secondary" size="sm" onClick={() => fileRef.current?.click()} loading={uploading}>
              Upload
            </Button>
            <button type="button" className="td-add-link" onClick={() => setLinkOpen(true)}>
              + Link
            </button>
          </div>
        )}
      </div>

      {!clientId && (
        <div className="td-muted td-doc-note">
          This task has no client, so uploads are filed at firm level.
        </div>
      )}

      {loading ? (
        <Skeleton.Text lines={3} />
      ) : docs.length === 0 ? (
        <div className="td-empty">
          <div className="td-empty-icon">📄</div>
          <div className="td-empty-text">No documents yet</div>
          <div className="td-muted">Upload a file or attach a link — it&apos;s stored in the firm&apos;s document store.</div>
        </div>
      ) : (
        <div className="td-doc-list">
          {docs.map((d) => {
            const id = d.id || d._id;
            return (
              <div key={id} className="td-doc-row">
                <button type="button" className="td-doc-main" onClick={() => download(d)}>
                  <span className="td-doc-name">
                    {d.source === 'link' ? '🔗' : '📄'} {d.title || d.fileName}
                  </span>
                  <span className="td-muted td-doc-meta">
                    {d.category}
                    {d.sizeBytes ? ` · ${fmtSize(d.sizeBytes)}` : ''}
                    {d.uploadedByName ? ` · ${d.uploadedByName}` : ''}
                    {` · ${fmtDate(d.uploadedAt)}`}
                  </span>
                </button>
                {!locked && (
                  <button type="button" className="td-doc-remove" onClick={() => remove(d)} aria-label="Remove document">
                    ×
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Modal
        isOpen={linkOpen}
        onClose={() => setLinkOpen(false)}
        title="Attach a link"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setLinkOpen(false)}>Cancel</Button>
            <Button variant="primary" onClick={addLink}>Add</Button>
          </>
        }
      >
        <Input
          label="URL"
          autoFocus
          placeholder="https://…"
          value={linkForm.fileUrl}
          onChange={(e) => setLinkForm({ ...linkForm, fileUrl: e.target.value })}
        />
        <Input
          label="Name"
          placeholder="Defaults to the URL"
          value={linkForm.fileName}
          onChange={(e) => setLinkForm({ ...linkForm, fileName: e.target.value })}
        />
        <Select
          label="Category"
          value={linkForm.category}
          onChange={(e) => setLinkForm({ ...linkForm, category: e.target.value })}
        >
          {categories.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
        </Select>
      </Modal>
    </Card>
  );
}

export default TaskDocumentsTab;
